import {useRef, useState, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {useNavigate} from 'react-router-dom'
import {Button} from '@/components/ui/button'
import {Checkbox} from '@/components/ui/checkbox'
import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger} from '@/components/ui/dialog'
import {Input} from '@/components/ui/input'
import {PorQue} from '@/components/ui/por-que'
import {Tag} from '@/components/ui/tag'
import {Textarea} from '@/components/ui/textarea'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {FieldError} from '@/components/field-error'
import {HelpIcon} from '@/components/help-icon'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {
  useConceptos,
  useLeerConcepto,
  useRegistrarConcepto,
  useRadicadosVivos,
  type Concepto,
  type Conceptos,
  type Lectura,
  type RestriccionWrite,
} from '@/hooks/salud/medicina_laboral/use-conceptos'
import {
  EVIDENCE_ACCEPT,
  EVIDENCE_ALLOWED_TYPES,
  EVIDENCE_MAX_BYTES,
  evidenceTooBig,
  mintUploadTarget,
  uploadToStorage,
} from '@/hooks/sgsst/evidencias/use-evidence'
import type {ConceptoAptitud, ExamenTipo} from '@/hooks/sgsst/evidencias/use-examenes'
import {esConceptoDuplicado, esFechaFuturaSinConfirmar, translateApiError} from '@/lib/errors'
import {formatDateOnly} from '@/lib/risk'
import {validateRequired} from '@/lib/validation'
import {tope} from '@/lib/campos-texto'

const TIPOS: ExamenTipo[] = ['ingreso', 'periodico', 'cambio_ocupacion', 'egreso', 'post_incapacidad', 'reintegro']

// «No apto» no está: la Res. 1843 de 2025 lo retiró del lenguaje del concepto y solo entra
// como transcripción de un certificado que lo trae escrito.
const VEREDICTOS: ConceptoAptitud[] = ['apto', 'apto_con_restricciones', 'aplazado', 'sin_concepto']

const EN_CURSO: ConceptoAptitud[] = ['aplazado', 'sin_concepto']

const SELECT_CLASS =
  'w-full rounded-lg border border-input bg-background px-3 py-2 text-sm shadow-sm focus:border-ring focus:outline-none'

type SoporteSubido = {object_key: string; filename: string; content_type: string; size_bytes: number}

const RESTRICCION_NUEVA: RestriccionWrite = {
  clase: 'restriccion',
  texto: '',
  temporalidad: 'permanente',
  fecha_fin: null,
  origen_campo: 'escrita',
}

/** Sin fecha de fin una restricción temporal queda viva para siempre (el backend da 422). Si el
 * texto se reescribe, la procedencia se degrada: deja de ser sugerida y suelta su fragmento. */
function RestriccionesField({
  restricciones,
  errores,
  onChange,
}: {
  restricciones: RestriccionWrite[]
  errores: number[]
  onChange: (next: RestriccionWrite[]) => void
}) {
  const {t} = useTranslation()

  function actualizar(index: number, patch: Partial<RestriccionWrite>) {
    onChange(
      restricciones.map((r, i) => {
        if (i !== index) return r
        if (patch.texto === undefined || r.origen_campo !== 'ia_sugerida') return {...r, ...patch}
        return {...r, ...patch, origen_campo: 'escrita', fragmento: null, pagina: null}
      })
    )
  }

  return (
    <div className="space-y-3">
      <span className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
        {t('concepto.registrar.restriccionesLabel')}
      </span>
      {restricciones.map((restriccion, index) => (
        <div key={index} className="space-y-2 rounded-lg border border-border p-3">
          <Input
            // TEXTO2000: `RestriccionWrite.texto` es `Field(max_length=2000)`.
            maxLength={tope('TEXTO2000')}
            value={restriccion.texto}
            data-testid={`concepto-restriccion-texto-${index}`}
            placeholder={t('concepto.registrar.restriccionTextoPlaceholder')}
            onChange={(e) => actualizar(index, {texto: e.target.value})}
          />
          <select
            value={restriccion.clase}
            data-testid={`concepto-restriccion-clase-${index}`}
            onChange={(e) => actualizar(index, {clase: e.target.value as RestriccionWrite['clase']})}
            className={SELECT_CLASS}>
            <option value="restriccion">{t('concepto.clase.restriccion')}</option>
            <option value="recomendacion">{t('concepto.clase.recomendacion')}</option>
          </select>
          <select
            value={restriccion.temporalidad}
            data-testid={`concepto-restriccion-temporalidad-${index}`}
            onChange={(e) =>
              actualizar(index, {
                temporalidad: e.target.value as RestriccionWrite['temporalidad'],
                // Volver a «permanente» borra la fecha: las dos afirmaciones juntas las rechaza el backend.
                fecha_fin: e.target.value === 'temporal' ? restriccion.fecha_fin : null,
              })
            }
            className={SELECT_CLASS}>
            <option value="permanente">{t('concepto.temporalidad.permanente')}</option>
            <option value="temporal">{t('concepto.temporalidad.temporal')}</option>
          </select>
          {restriccion.temporalidad === 'temporal' && (
            <label className="block space-y-1">
              <span className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
                {t('concepto.registrar.fechaFinLabel')}
              </span>
              <Input
                type="date"
                value={restriccion.fecha_fin ?? ''}
                data-testid={`concepto-restriccion-fecha-fin-${index}`}
                onChange={(e) => actualizar(index, {fecha_fin: e.target.value || null})}
              />
              <FieldError
                error={errores.includes(index) ? t('concepto.errors.restriccionSinFechaCliente') : undefined}
              />
            </label>
          )}
          <Button
            type="button"
            size="sm"
            variant="ghost"
            data-testid={`concepto-restriccion-quitar-${index}`}
            onClick={() => onChange(restricciones.filter((_, i) => i !== index))}>
            {t('concepto.registrar.quitarRestriccion')}
          </Button>
        </div>
      ))}
      <Button
        type="button"
        size="sm"
        variant="outline"
        data-testid="concepto-restriccion-agregar"
        onClick={() => onChange([...restricciones, {...RESTRICCION_NUEVA}])}>
        {t('concepto.registrar.agregarRestriccion')}
      </Button>
    </div>
  )
}

/** Salida del 409 `conceptoDuplicado` dentro del mismo diálogo, con el formulario intacto. */
function ResolverDuplicado({
  vivos,
  reemplazaA,
  onElegir,
}: {
  vivos: Concepto[]
  reemplazaA: string | null
  onElegir: (reemplazaA: string | null, distinto: boolean) => void
}) {
  const {t} = useTranslation()
  return (
    <div className="space-y-2 rounded-lg border border-border p-3" data-testid="concepto-duplicado-salidas">
      <p className="text-sm text-foreground">{t('concepto.duplicado.pregunta')}</p>
      {vivos.map((vivo) => (
        <label key={vivo.id} className="flex items-start gap-2 text-sm">
          <input
            type="radio"
            name="concepto-duplicado"
            checked={reemplazaA === vivo.id}
            data-testid={`concepto-duplicado-reemplaza-${vivo.id}`}
            onChange={() => onElegir(vivo.id, false)}
          />
          <span className="text-muted-foreground">
            {t('concepto.duplicado.reemplaza', {
              fecha: formatDateOnly(vivo.fecha),
              veredicto: t(`concepto.veredicto.${vivo.concepto_aptitud}`),
            })}
          </span>
        </label>
      ))}
      <label className="flex items-start gap-2 text-sm">
        <input
          type="radio"
          name="concepto-duplicado"
          data-testid="concepto-duplicado-distinto"
          onChange={() => onElegir(null, true)}
        />
        <span className="text-muted-foreground">{t('concepto.duplicado.distinto')}</span>
      </label>
    </div>
  )
}

export function RegistrarConceptoDialog({businessId, employeeId}: {businessId: string; employeeId: string}) {
  const {t} = useTranslation()
  const [open, setOpen] = useState(false)
  const [descartando, setDescartando] = useState(false)
  const [tipo, setTipo] = useState<ExamenTipo>('periodico')
  const [veredicto, setVeredicto] = useState<ConceptoAptitud>('apto')
  const [textual, setTextual] = useState(false)
  const [fecha, setFecha] = useState('')
  const [fechaError, setFechaError] = useState<string | undefined>()
  // «Posterior a hoy» lo decide el backend (`bogota_today`), nunca el reloj del navegador.
  const [fechaFutura, setFechaFutura] = useState(false)
  const [fechaConfirmada, setFechaConfirmada] = useState(false)
  const [restricciones, setRestricciones] = useState<RestriccionWrite[]>([])
  const [restriccionErrores, setRestriccionErrores] = useState<number[]>([])
  const [motivo, setMotivo] = useState('')
  const [fechaControl, setFechaControl] = useState('')
  const [medico, setMedico] = useState('')
  const [cargoLeido, setCargoLeido] = useState('')
  const [radicado, setRadicado] = useState('')
  // Sin `respuesta_texto` H10 no tiene con qué comparar lo pedido con lo contestado.
  const [respuestaTexto, setRespuestaTexto] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [duplicado, setDuplicado] = useState(false)
  const [reemplazaA, setReemplazaA] = useState<string | null>(null)
  const [esDistinto, setEsDistinto] = useState(false)
  const [atestacion, setAtestacion] = useState(false)
  const [soporte, setSoporte] = useState<SoporteSubido | null>(null)
  const [progress, setProgress] = useState<number | null>(null)
  const [sinLectura, setSinLectura] = useState(false)
  // Sin este nodo los popovers se portan a `document.body` y Escape cierra el diálogo entero.
  const [dialogo, setDialogo] = useState<HTMLElement | null>(null)
  // `campos_origen` y el fragmento/página del cargo leído son la procedencia que pide §3.
  const [lectura, setLectura] = useState<Lectura | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()
  const registrar = useRegistrarConcepto(businessId, employeeId)
  const [retenido, setRetenido] = useState<Conceptos['retenido']>(null)
  const leer = useLeerConcepto(businessId, employeeId)
  const {data: conceptos} = useConceptos(businessId, employeeId)
  const radicadosVivos = useRadicadosVivos(businessId, open ? employeeId : null)
  const enCurso = EN_CURSO.includes(veredicto)
  const conRestricciones = veredicto === 'apto_con_restricciones' && !textual
  const radicadoDesconocido =
    !!radicado.trim() &&
    radicadosVivos.data !== undefined &&
    !radicadosVivos.data.some((vivo) => vivo.toUpperCase() === radicado.trim().toUpperCase())
  const sucio =
    !!soporte || restricciones.length > 0 || !!fecha || !!medico || !!cargoLeido || !!radicado || !!respuestaTexto

  /** La lectura es una sugerencia: lo no leído llega vacío y, sin `modelo`, el formulario no cambia. */
  function prefill(leida: Lectura) {
    setSinLectura(leida.modelo === null)
    if (leida.modelo === null) return
    setLectura(leida)
    if (leida.veredicto && leida.veredicto !== 'no_apto') setVeredicto(leida.veredicto)
    if (leida.fecha) setFecha(leida.fecha)
    if (leida.fecha_control) setFechaControl(leida.fecha_control)
    if (leida.medico_nombre) setMedico(leida.medico_nombre)
    if (leida.cargo_leido_texto) setCargoLeido(leida.cargo_leido_texto)
    setRestricciones(
      leida.restricciones.map((r) => ({
        clase: r.clase,
        texto: r.texto,
        temporalidad: r.temporalidad,
        fecha_fin: r.fecha_fin,
        origen_campo: 'ia_sugerida',
        fragmento: r.fragmento,
        pagina: r.pagina,
      }))
    )
  }

  async function pickFile(files: FileList | null) {
    const picked = files?.[0]
    if (!picked) return
    setError(null)
    if (!EVIDENCE_ALLOWED_TYPES.has(picked.type)) return setError(t('errors.evidenceBadFormat'))
    if (picked.size > EVIDENCE_MAX_BYTES) return setError(evidenceTooBig(t, picked))
    try {
      setProgress(0)
      const target = await mintUploadTarget(businessId, picked)
      await uploadToStorage(target, picked, setProgress)
      const subido = {
        object_key: target.object_key,
        filename: picked.name,
        content_type: picked.type,
        size_bytes: picked.size,
      }
      setSoporte(subido)
      setProgress(null)
      prefill(await leer.mutateAsync(subido))
    } catch (err) {
      setProgress(null)
      setError(translateApiError(err))
    }
  }

  function reset() {
    setTipo('periodico')
    setVeredicto('apto')
    setTextual(false)
    setFecha('')
    setFechaError(undefined)
    setFechaFutura(false)
    setFechaConfirmada(false)
    setRestricciones([])
    setRestriccionErrores([])
    setMotivo('')
    setFechaControl('')
    setMedico('')
    setCargoLeido('')
    setRadicado('')
    setError(null)
    setDuplicado(false)
    setReemplazaA(null)
    setEsDistinto(false)
    setAtestacion(false)
    setSoporte(null)
    setProgress(null)
    setSinLectura(false)
    setLectura(null)
    if (inputRef.current) inputRef.current.value = ''
  }

  function cambiarVeredicto(next: ConceptoAptitud) {
    setVeredicto(next)
    // Sin esto quedaría un APTO con una restricción viva en la base y sin camino para quitarla.
    if (next !== 'apto_con_restricciones') {
      setRestricciones([])
      setRestriccionErrores([])
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    const err = validateRequired(fecha)
    setFechaError(err ?? undefined)
    if (err) return
    const malas = restricciones
      .map((r, i) => (conRestricciones && r.temporalidad === 'temporal' && !r.fecha_fin ? i : -1))
      .filter((i) => i >= 0)
    setRestriccionErrores(malas)
    if (malas.length > 0) return
    setError(null)
    try {
      const respuesta = await registrar.mutateAsync({
        tipo,
        concepto_aptitud: textual ? 'no_apto' : veredicto,
        fecha,
        fecha_confirmada: fechaConfirmada,
        veredicto_textual_certificado: textual,
        restricciones: conRestricciones ? restricciones : [],
        aplazado_motivo: motivo || null,
        fecha_control: fechaControl || null,
        medico_nombre: medico || null,
        cargo_leido_texto: cargoLeido || null,
        // Si el texto se reescribió, el fragmento ya no lo sostiene y viaja vacío (§3).
        cargo_leido_fragmento: cargoLeido === lectura?.cargo_leido_texto ? lectura.cargo_leido_fragmento : null,
        cargo_leido_pagina: cargoLeido === lectura?.cargo_leido_texto ? lectura.cargo_leido_pagina : null,
        campos_origen: lectura?.campos_origen ?? {},
        radicado: radicado || null,
        respuesta_texto: respuestaTexto.trim() || null,
        atestacion,
        // Seguro por la asimetría de §5: la IA solo puede agregar retenciones, nunca quitarlas.
        datos_clinicos_leidos: lectura?.tiene_datos_clinicos ?? false,
        reemplaza_a_examen_id: reemplazaA,
        es_examen_distinto: esDistinto,
        respuesta_origen: 'cargada',
        ...(soporte ?? {}),
      })
      setOpen(false)
      setRetenido(respuesta.retenido ?? null)
    } catch (err) {
      const traducido = translateApiError(err)
      // Ninguno cierra ni limpia: son preguntas que se contestan con el formulario escrito.
      if (esConceptoDuplicado(err)) setDuplicado(true)
      if (esFechaFuturaSinConfirmar(err)) setFechaFutura(true)
      setError(traducido)
    }
  }

  const vivos = (conceptos?.items ?? []).filter((c) => !c.anulado_en)

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next && sucio) return setDescartando(true)
          setOpen(next)
          if (next) reset()
        }}>
        <DialogTrigger asChild>
          <Button size="sm" data-testid="concepto-registrar-abrir">
            {t('concepto.registrar.action')}
          </Button>
        </DialogTrigger>
        {/* Sin este alto máximo el botón de registrar queda fuera de la pantalla. */}
        <DialogContent ref={setDialogo} className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('concepto.registrar.title')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" noValidate>
            <label className="block space-y-1">
              <span className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
                {t('concepto.registrar.tipoLabel')}
              </span>
              <select
                value={tipo}
                data-testid="concepto-registrar-tipo"
                onChange={(e) => setTipo(e.target.value as ExamenTipo)}
                className={SELECT_CLASS}>
                {TIPOS.map((valor) => (
                  <option key={valor} value={valor}>
                    {t(`empleadoExamenes.tipo.${valor}`)}
                  </option>
                ))}
              </select>
            </label>

            <div className="space-y-2">
              <span className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
                {t('concepto.registrar.veredictoLabel')}
              </span>
              <select
                value={veredicto}
                disabled={textual}
                data-testid="concepto-registrar-veredicto"
                onChange={(e) => cambiarVeredicto(e.target.value as ConceptoAptitud)}
                className={SELECT_CLASS}>
                {VEREDICTOS.map((valor) => (
                  <option key={valor} value={valor}>
                    {t(`concepto.veredicto.${valor}`)}
                  </option>
                ))}
              </select>
              {/* El disparador va fuera del `<label>`: dentro entraría en el nombre accesible del campo. */}
              <div className="flex items-start gap-1.5 text-sm">
                <label className="flex items-start gap-2">
                  <Checkbox
                    checked={textual}
                    data-testid="concepto-registrar-textual"
                    onCheckedChange={(next) => {
                      setTextual(next === true)
                      if (next === true) {
                        setRestricciones([])
                        setRestriccionErrores([])
                      }
                    }}
                  />
                  <span className="text-muted-foreground">{t('concepto.registrar.textualLabel')}</span>
                </label>
                <HelpIcon
                  label={t('concepto.registrar.textualTipLabel')}
                  testId="concepto-registrar-textual-tip"
                  contenedor={dialogo}>
                  {t('concepto.registrar.textualTip')}
                </HelpIcon>
              </div>
            </div>

            <label className="block space-y-1">
              <span className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
                {t('concepto.registrar.fechaLabel')}
              </span>
              <Input
                type="date"
                value={fecha}
                data-testid="concepto-registrar-fecha"
                onChange={(e) => {
                  setFecha(e.target.value)
                  setFechaError(undefined)
                }}
                onBlur={(e) => setFechaError(validateRequired(e.target.value) ?? undefined)}
              />
              <FieldError error={fechaError} />
            </label>

            {fechaFutura && (
              <label className="flex items-start gap-2 text-sm" data-testid="concepto-registrar-fecha-futura">
                <Checkbox
                  checked={fechaConfirmada}
                  data-testid="concepto-registrar-fecha-confirmada"
                  onCheckedChange={(next) => setFechaConfirmada(next === true)}
                />
                <span className="text-muted-foreground">{t('concepto.fechaConfirmarLabel')}</span>
              </label>
            )}

            {conRestricciones && (
              <RestriccionesField
                restricciones={restricciones}
                errores={restriccionErrores}
                onChange={setRestricciones}
              />
            )}

            {enCurso && !textual && (
              <>
                <label className="block space-y-1">
                  <span className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
                    {t('concepto.registrar.motivoLabel')}
                  </span>
                  <Input
                    maxLength={tope('NOTA')}
                    value={motivo}
                    data-testid="concepto-registrar-motivo"
                    onChange={(e) => setMotivo(e.target.value)}
                  />
                </label>
                <label className="block space-y-1">
                  <span className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
                    {t('concepto.registrar.fechaControlLabel')}
                  </span>
                  <Input
                    type="date"
                    value={fechaControl}
                    data-testid="concepto-registrar-fecha-control"
                    onChange={(e) => setFechaControl(e.target.value)}
                  />
                </label>
              </>
            )}

            <label className="block space-y-1">
              <span className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
                {t('concepto.registrar.medicoLabel')}
              </span>
              <Input
                maxLength={tope('NOM')}
                value={medico}
                data-testid="concepto-registrar-medico"
                onChange={(e) => setMedico(e.target.value)}
              />
            </label>

            <label className="block space-y-1">
              <span className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
                {t('concepto.registrar.cargoLeidoLabel')}
              </span>
              <Input
                maxLength={tope('CARGO')}
                value={cargoLeido}
                data-testid="concepto-registrar-cargo-leido"
                onChange={(e) => setCargoLeido(e.target.value)}
              />
            </label>

            <label className="block space-y-1">
              <span className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
                {t('concepto.registrar.radicadoLabel')}
              </span>
              <Input
                maxLength={tope('CODE')}
                value={radicado}
                data-testid="concepto-registrar-radicado"
                onChange={(e) => setRadicado(e.target.value)}
              />
              {/* Se avisa antes de registrar: un radicado desconocido entraría «sin identificar»
                  sin que nadie lo note. */}
              {radicadoDesconocido && (
                <p className="text-xs text-destructive" data-testid="concepto-radicado-desconocido">
                  {t('concepto.radicadoDesconocido')}
                </p>
              )}
            </label>

            {/* Opcional a propósito: registrar el hecho nunca se bloquea. */}
            {/* `htmlFor` en vez de envolver, para que el `HelpIcon` no entre en el nombre del campo. */}
            <div className="space-y-1">
              <span className="flex items-center gap-1.5 font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
                <label htmlFor="concepto-registrar-texto">{t('concepto.registrar.textoLabel')}</label>
                <HelpIcon
                  label={t('concepto.registrar.textoTipLabel')}
                  testId="concepto-registrar-texto-tip"
                  contenedor={dialogo}>
                  {t('concepto.registrar.textoTip')}
                </HelpIcon>
              </span>
              <Textarea
                maxLength={tope('NOTA')}
                id="concepto-registrar-texto"
                rows={4}
                value={respuestaTexto}
                data-testid="concepto-registrar-texto"
                onChange={(e) => setRespuestaTexto(e.target.value)}
              />
            </div>

            <div className="space-y-1">
              <span className="flex items-center gap-1.5 font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
                {t('concepto.registrar.soporteLabel')}
                <HelpIcon
                  label={t('common.help.iconLabelFor', {campo: t('concepto.registrar.soporteLabel')})}
                  testId="concepto-registrar-soporte-tip"
                  contenedor={dialogo}>
                  {t('concepto.registrar.soporteTip')}
                </HelpIcon>
              </span>
              <input
                ref={inputRef}
                type="file"
                accept={EVIDENCE_ACCEPT}
                hidden
                data-testid="concepto-registrar-soporte-input"
                onChange={(e) => pickFile(e.target.files)}
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                data-testid="concepto-registrar-soporte-elegir"
                onClick={() => inputRef.current?.click()}>
                {soporte ? soporte.filename : t('concepto.registrar.soporteElegir')}
              </Button>
              {progress !== null && (
                <div className="h-1.5 w-32 overflow-hidden rounded-full bg-secondary">
                  <div className="h-full rounded-full bg-primary transition-all" style={{width: `${progress}%`}} />
                </div>
              )}
              {sinLectura && (
                <Tag tone="neutral" data-testid="concepto-registrar-sin-lectura">
                  {t('concepto.registrar.sinLectura')}
                </Tag>
              )}
            </div>

            {/* Sin mostrar el contenido (§5), y antes de la casilla: se sabe antes de atestiguar. */}
            {lectura?.tiene_datos_clinicos && (
              <p className="notice-amber rounded-lg px-3 py-2 text-sm" data-testid="concepto-registrar-clinico">
                {t('concepto.registrar.datosClinicos')}
              </p>
            )}

            {soporte && (
              <>
                <label className="flex items-start gap-2 text-sm">
                  <Checkbox
                    checked={atestacion}
                    data-testid="concepto-registrar-atestacion"
                    onCheckedChange={(next) => setAtestacion(next === true)}
                  />
                  <span className="text-muted-foreground">{t('concepto.registrar.atestacionLabel')}</span>
                </label>
                {/* Sin la firma el registro no se rechaza: el papel queda retenido (§4). */}
                {!atestacion && (
                  <div className="flex flex-wrap items-center gap-2">
                    <Tag tone="amber" data-testid="concepto-registrar-sin-atestacion">
                      {t('concepto.registrar.sinAtestacion')}
                    </Tag>
                    <PorQue
                      label={t('concepto.registrar.porQueLabel')}
                      ariaLabel={t('concepto.registrar.sinAtestacionPorqueAria')}
                      testId="concepto-registrar-sin-atestacion-porque"
                      contenedor={dialogo}>
                      {t('concepto.registrar.sinAtestacionPorque')}
                    </PorQue>
                  </div>
                )}
              </>
            )}

            {duplicado && (
              <ResolverDuplicado
                vivos={vivos}
                reemplazaA={reemplazaA}
                onElegir={(id, distinto) => {
                  setReemplazaA(id)
                  setEsDistinto(distinto)
                  setError(null)
                }}
              />
            )}

            <FormDialogFooter
              error={error}
              pending={registrar.isPending || progress !== null || leer.isPending}
              submitLabel={t('concepto.registrar.submit')}
              onCancel={() => (sucio ? setDescartando(true) : setOpen(false))}
            />
          </form>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={retenido != null}
        onOpenChange={(next) => !next && setRetenido(null)}
        title={t('concepto.retenido.titulo')}
        description={t('concepto.retenido.cuerpo', {
          fecha: retenido ? formatDateOnly(retenido.suprimir_el) : '',
          motivo: retenido ? t(`cuarentena.motivos.${retenido.retencion_motivo}`) : '',
        })}
        confirmLabel={t('concepto.retenido.enlace')}
        cancelLabel={t('concepto.retenido.cerrar')}
        pending={false}
        onConfirm={() => {
          setRetenido(null)
          navigate(`/businesses/${businessId}/cuarentena`)
        }}
      />
      <ConfirmDialog
        open={descartando}
        onOpenChange={(next) => !next && setDescartando(false)}
        title={t('concepto.descartar.title')}
        description={t('concepto.descartar.description')}
        confirmLabel={t('common.discard')}
        cancelLabel={t('common.cancel')}
        pending={false}
        onConfirm={() => {
          setDescartando(false)
          setOpen(false)
        }}
      />
    </>
  )
}
